import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Settings,
  List,
  Grid,
  Calendar as CalendarIcon,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  CheckCircle,
  Clock,
  Send,
  Upload,
  Download
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO,
  setHours,
  setMinutes
} from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import DashboardLayout from '../components/DashboardLayout';
import { api, isUpgradeError } from '../context/AuthContext';
import { toast } from 'sonner';

const PLATFORM_ICONS = {
  facebook: '📘',
  instagram: '📸',
  twitter: '🐦',
  linkedin: '💼',
  tiktok: '🎵',
  youtube: '📺',
  pinterest: '📌',
  threads: '🧵',
  bluesky: '🦋'
};

const STATUS_STYLES = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
  pending_approval: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-400' },
  approved: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-400' },
  scheduled: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  published: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-400' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-400' }
};

const CalendarViewPage = () => {
  const { calendarId } = useParams();
  const navigate = useNavigate();
  const [calendar, setCalendar] = useState(null);
  const [posts, setPosts] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAutofill, setShowAutofill] = useState(false);
  const [autofillLoading, setAutofillLoading] = useState(false);
  const [autofillSuggestions, setAutofillSuggestions] = useState(null);
  const [autofillSettings, setAutofillSettings] = useState({
    industry: 'general',
    tone: 'professional',
    posts_per_day: 1,
    include_weekends: false,
  });

  const fetchData = useCallback(async () => {
    try {
      const [calendarRes, postsRes] = await Promise.all([
        api.get(`/calendars/${calendarId}`),
        api.get(`/posts?calendar_id=${calendarId}`)
      ]);
      setCalendar(calendarRes.data);
      setPosts(postsRes.data);
    } catch (error) {
      toast.error('Failed to load calendar');
      navigate('/calendars');
    } finally {
      setLoading(false);
    }
  }, [calendarId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { draggableId, source, destination } = result;
    
    // Get the date from the droppable ID
    const targetDate = destination.droppableId;
    
    // Find the post
    const postIndex = posts.findIndex(p => p.post_id === draggableId);
    if (postIndex === -1) return;
    
    const post = posts[postIndex];
    
    // Calculate new scheduled time (keep the same time, change date)
    let newScheduledAt;
    if (post.scheduled_at) {
      const originalDate = parseISO(post.scheduled_at);
      const targetDateObj = parseISO(targetDate);
      newScheduledAt = setHours(setMinutes(targetDateObj, originalDate.getMinutes()), originalDate.getHours());
    } else {
      // Default to 9 AM on the target date
      newScheduledAt = setHours(parseISO(targetDate), 9);
    }
    
    // Update locally first for smooth UX
    const updatedPosts = [...posts];
    updatedPosts[postIndex] = {
      ...post,
      scheduled_at: newScheduledAt.toISOString(),
      status: post.status === 'draft' ? 'scheduled' : post.status
    };
    setPosts(updatedPosts);
    
    // Update on server
    try {
      await api.put(`/posts/${draggableId}`, {
        scheduled_at: newScheduledAt.toISOString(),
        status: post.status === 'draft' ? 'scheduled' : undefined
      });
      toast.success('Post rescheduled');
    } catch (error) {
      // Revert on error
      setPosts(posts);
      toast.error('Failed to reschedule post');
    }
  };

  const handleDuplicatePost = async (postId) => {
    try {
      const response = await api.post(`/posts/${postId}/duplicate`);
      setPosts([...posts, response.data]);
      toast.success('Post duplicated');
    } catch (error) {
      toast.error('Failed to duplicate post');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await api.delete(`/posts/${postId}`);
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handlePublishPost = async (postId) => {
    try {
      await api.post(`/posts/${postId}/publish`);
      fetchData();
      toast.success('Post published');
    } catch (error) {
      toast.error('Failed to publish post');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('calendar_id', calendarId);
    
    try {
      const response = await api.post('/bulk/upload-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.error_count > 0) {
        toast.warning(`Created ${response.data.created_count} posts with ${response.data.error_count} errors`);
      } else {
        toast.success(`Created ${response.data.created_count} posts`);
      }
      
      fetchData();
      setShowBulkUpload(false);
    } catch (error) {
      toast.error('Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/bulk/csv-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'stellisocial_bulk_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleAutofillGenerate = async () => {
    setAutofillLoading(true);
    try {
      const res = await api.post('/ai/calendar-autofill', {
        calendar_id: calendarId,
        ...autofillSettings,
      });
      setAutofillSuggestions(res.data);
    } catch (error) {
      if (!isUpgradeError(error)) toast.error(error.response?.data?.detail || 'Failed to generate content plan');
    } finally {
      setAutofillLoading(false);
    }
  };

  const handleAutofillApply = async () => {
    if (!autofillSuggestions?.suggestions) return;
    try {
      const res = await api.post(`/ai/calendar-autofill/apply?calendar_id=${calendarId}`, autofillSuggestions.suggestions);
      toast.success(`${res.data.created} posts created as drafts`);
      setShowAutofill(false);
      setAutofillSuggestions(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to apply suggestions');
    }
  };

  const filteredPosts = statusFilter === 'all'
    ? posts
    : posts.filter(p => p.status === statusFilter);

  const getPostsForDate = (date) => {
    return filteredPosts.filter(post => {
      if (!post.scheduled_at) return false;
      return isSameDay(parseISO(post.scheduled_at), date);
    });
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const calendarDays = [];
  let day = calendarStart;
  while (day <= calendarEnd) {
    calendarDays.push(day);
    day = addDays(day, 1);
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="calendar-view-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/calendars">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${calendar?.brand_color}20` }}
              >
                <CalendarIcon className="h-5 w-5" style={{ color: calendar?.brand_color }} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">{calendar?.name}</h1>
                <p className="text-sm text-slate-500">{posts.length} posts</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* View Mode Tabs */}
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList className="bg-slate-100">
                <TabsTrigger value="month" className="data-[state=active]:bg-white">
                  <Grid className="h-4 w-4 mr-1" />
                  Month
                </TabsTrigger>
                <TabsTrigger value="list" className="data-[state=active]:bg-white">
                  <List className="h-4 w-4 mr-1" />
                  List
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => setShowBulkUpload(true)}
              data-testid="bulk-upload-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Bulk Upload
            </Button>

            <Button
              variant="outline"
              onClick={() => setShowAutofill(true)}
              data-testid="ai-autofill-btn"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            >
              <span className="mr-2">⚡</span>
              AI Auto-fill
            </Button>

            <Link to={`/calendars/${calendarId}/settings`}>
              <Button variant="outline" size="icon">
                <Settings className="h-4 w-4" />
              </Button>
            </Link>

            <Link to={`/posts/new?calendar=${calendarId}`}>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="calendar-create-post-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Post
              </Button>
            </Link>
          </div>
        </div>

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              onClick={() => setCurrentDate(new Date())}
              className="text-emerald-600"
            >
              Today
            </Button>
          </div>
          <h2 className="text-xl font-semibold text-slate-900">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
        </div>

        {/* Calendar View with Drag and Drop */}
        {viewMode === 'month' ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Card className="border-slate-100 overflow-hidden">
              <CardContent className="p-0">
                {/* Day Headers */}
                <div className="grid grid-cols-7 border-b border-slate-100">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                    <div
                      key={day}
                      className="p-3 text-center text-sm font-medium text-slate-500 bg-slate-50"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7">
                  {calendarDays.map((day, i) => {
                    const dayPosts = getPostsForDate(day);
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isCurrentDay = isToday(day);
                    const dateStr = format(day, 'yyyy-MM-dd');

                    return (
                      <Droppable key={dateStr} droppableId={dateStr}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`min-h-[120px] p-2 border-b border-r border-slate-100 transition-colors ${
                              !isCurrentMonth ? 'bg-slate-50' : 'bg-white'
                            } ${isCurrentDay ? 'bg-emerald-50' : ''} ${
                              snapshot.isDraggingOver ? 'bg-emerald-100 border-emerald-300' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span
                                className={`text-sm font-medium ${
                                  isCurrentDay
                                    ? 'w-7 h-7 bg-emerald-500 text-white rounded-full flex items-center justify-center'
                                    : isCurrentMonth
                                    ? 'text-slate-900'
                                    : 'text-slate-400'
                                }`}
                              >
                                {format(day, 'd')}
                              </span>
                              {isCurrentMonth && dayPosts.length === 0 && (
                                <Link to={`/posts/new?calendar=${calendarId}&date=${dateStr}`}>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 opacity-40 hover:opacity-100 transition-opacity">
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </Link>
                              )}
                            </div>

                            <div className="space-y-1">
                              {dayPosts.slice(0, 3).map((post, index) => (
                                <Draggable key={post.post_id} draggableId={post.post_id} index={index}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`group ${snapshot.isDragging ? 'opacity-70' : ''}`}
                                    >
                                      <Link
                                        to={`/posts/${post.post_id}`}
                                        className={`block px-2 py-1 rounded text-xs truncate ${STATUS_STYLES[post.status].bg} ${STATUS_STYLES[post.status].text} hover:opacity-80 transition-opacity cursor-grab active:cursor-grabbing`}
                                        onClick={(e) => snapshot.isDragging && e.preventDefault()}
                                      >
                                        <span className="mr-1">
                                          {post.platforms.map(p => PLATFORM_ICONS[p] || '📱').join('')}
                                        </span>
                                        {post.content.substring(0, 30)}
                                      </Link>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {dayPosts.length > 3 && (
                                <p className="text-xs text-slate-500 text-center">
                                  +{dayPosts.length - 3} more
                                </p>
                              )}
                            </div>
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </DragDropContext>
        ) : (
          /* List View */
          <Card className="border-slate-100">
            <CardContent className="p-0">
              {filteredPosts.length === 0 ? (
                <div className="text-center py-16">
                  <CalendarIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No posts yet</h3>
                  <p className="text-slate-500 mb-4">Create your first post for this calendar</p>
                  <Link to={`/posts/new?calendar=${calendarId}`}>
                    <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Post
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredPosts.map((post) => (
                    <motion.div
                      key={post.post_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-2 h-2 rounded-full ${STATUS_STYLES[post.status].dot}`} />
                            <Badge className={`${STATUS_STYLES[post.status].bg} ${STATUS_STYLES[post.status].text}`}>
                              {post.status.replace('_', ' ')}
                            </Badge>
                            <span className="text-sm text-slate-500">
                              {post.scheduled_at
                                ? format(parseISO(post.scheduled_at), 'MMM d, h:mm a')
                                : 'Not scheduled'}
                            </span>
                          </div>
                          <Link to={`/posts/${post.post_id}`}>
                            <p className="text-slate-900 hover:text-emerald-600 transition-colors line-clamp-2">
                              {post.content}
                            </p>
                          </Link>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-sm">
                              {post.platforms.map(p => PLATFORM_ICONS[p] || '📱').join(' ')}
                            </span>
                            {post.hashtags?.length > 0 && (
                              <span className="text-xs text-slate-500">
                                {post.hashtags.length} hashtags
                              </span>
                            )}
                          </div>
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/posts/${post.post_id}`)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicatePost(post.post_id)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            {post.status === 'approved' && (
                              <DropdownMenuItem onClick={() => handlePublishPost(post.post_id)}>
                                <Send className="h-4 w-4 mr-2" />
                                Publish Now
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleDeletePost(post.post_id)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bulk Upload Dialog */}
        <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Bulk Upload Posts</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <p className="text-sm text-slate-600">
                Upload a CSV file to create multiple posts at once. 
              </p>
              
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">CSV Format</h4>
                <p className="text-xs text-slate-600 mb-2">
                  Required columns: content, platforms
                </p>
                <p className="text-xs text-slate-600">
                  Optional columns: scheduled_at, hashtags, link
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={downloadTemplate}
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>

              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleBulkUpload}
                  className="hidden"
                  id="csv-upload"
                  disabled={uploading}
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-600">
                    {uploading ? 'Uploading...' : 'Click to upload CSV file'}
                  </p>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkUpload(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* AI Auto-fill Dialog */}
        <Dialog open={showAutofill} onOpenChange={setShowAutofill}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                <span className="mr-2">⚡</span>AI Content Calendar Auto-fill
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {!autofillSuggestions ? (
                <>
                  <p className="text-sm text-slate-600">
                    Generate a full week of content ideas powered by AI. Posts will be created as drafts.
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Industry / Niche</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="e.g. tech startups"
                        value={autofillSettings.industry}
                        onChange={(e) => setAutofillSettings(s => ({ ...s, industry: e.target.value }))}
                        data-testid="autofill-industry"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Tone</label>
                      <Select value={autofillSettings.tone} onValueChange={(v) => setAutofillSettings(s => ({ ...s, tone: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="casual">Casual</SelectItem>
                          <SelectItem value="humorous">Humorous</SelectItem>
                          <SelectItem value="inspirational">Inspirational</SelectItem>
                          <SelectItem value="educational">Educational</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Posts Per Day</label>
                      <Select value={String(autofillSettings.posts_per_day)} onValueChange={(v) => setAutofillSettings(s => ({ ...s, posts_per_day: parseInt(v) }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 per day</SelectItem>
                          <SelectItem value="2">2 per day</SelectItem>
                          <SelectItem value="3">3 per day</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autofillSettings.include_weekends}
                          onChange={(e) => setAutofillSettings(s => ({ ...s, include_weekends: e.target.checked }))}
                          className="rounded"
                        />
                        Include weekends
                      </label>
                    </div>
                  </div>

                  <Button
                    onClick={handleAutofillGenerate}
                    disabled={autofillLoading}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="autofill-generate-btn"
                  >
                    {autofillLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Generating content plan...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <span>⚡</span> Generate Week Plan
                      </span>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600">
                      {autofillSuggestions.total_slots} posts for {autofillSuggestions.week_start} to {autofillSuggestions.week_end}
                    </p>
                    <Badge variant="outline" className="text-emerald-700 border-emerald-200">
                      {autofillSuggestions.source === 'ai' ? 'AI Generated' : 'Mock Data'}
                    </Badge>
                  </div>

                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {autofillSuggestions.suggestions.map((sug, i) => (
                      <Card key={i} className="p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{PLATFORM_ICONS[sug.platform] || '📱'}</span>
                            <span className="text-xs font-medium text-slate-500">{sug.day} {sug.time}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">{sug.content_type}</Badge>
                        </div>
                        {sug.title && <p className="text-sm font-medium text-slate-900 mb-1">{sug.title}</p>}
                        <p className="text-sm text-slate-600">{sug.content}</p>
                        {sug.hashtags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {sug.hashtags.map((h, j) => (
                              <span key={j} className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                {h.startsWith('#') ? h : `#${h}`}
                              </span>
                            ))}
                          </div>
                        )}
                        {sug.reasoning && (
                          <p className="text-xs text-slate-400 mt-1 italic">{sug.reasoning}</p>
                        )}
                      </Card>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setAutofillSuggestions(null)}
                    >
                      Regenerate
                    </Button>
                    <Button
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                      onClick={handleAutofillApply}
                      data-testid="autofill-apply-btn"
                    >
                      Apply All as Drafts
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CalendarViewPage;
